const STORAGE_KEY = 'hanna-tags-view'

function normalizeView(view) {
  return {
    title: view.title || (view.meta && view.meta.title) || 'no-name',
    path: view.path,
    fullPath: view.fullPath || view.path,
    name: view.name,
    query: view.query || {},
    params: view.params || {},
    meta: view.meta || {}
  }
}

function loadPersistedState() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return { visitedViews: [], cachedViews: [] }
    }
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return { visitedViews: [], cachedViews: [] }
    }
    const parsed = JSON.parse(raw)
    return {
      visitedViews: Array.isArray(parsed.visitedViews) ? parsed.visitedViews : [],
      cachedViews: Array.isArray(parsed.cachedViews) ? parsed.cachedViews : []
    }
  } catch (error) {
    return { visitedViews: [], cachedViews: [] }
  }
}

function persistState(state) {
  if (typeof window === 'undefined' || !window.localStorage) return
  const payload = {
    visitedViews: state.visitedViews.map(normalizeView),
    cachedViews: state.cachedViews
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

function clearPersistedState() {
  if (typeof window === 'undefined' || !window.localStorage) return
  window.localStorage.removeItem(STORAGE_KEY)
}

const persistedState = loadPersistedState()

const state = {
  visitedViews: persistedState.visitedViews,
  cachedViews: persistedState.cachedViews
}

const mutations = {
  ADD_VISITED_VIEW: (state, view) => {
    if (state.visitedViews.some(v => v.path === view.path)) return
    state.visitedViews.push(normalizeView(view))
    persistState(state)
  },
  ADD_CACHED_VIEW: (state, view) => {
    if (state.cachedViews.includes(view.name)) return
    if (!view.meta.noCache) {
      state.cachedViews.push(view.name)
      persistState(state)
    }
  },

  DEL_VISITED_VIEW: (state, view) => {
    for (const [i, v] of state.visitedViews.entries()) {
      if (v.path === view.path) {
        state.visitedViews.splice(i, 1)
        persistState(state)
        break
      }
    }
  },
  DEL_CACHED_VIEW: (state, view) => {
    const index = state.cachedViews.indexOf(view.name)
    if (index > -1) {
      state.cachedViews.splice(index, 1)
      persistState(state)
    }
  },

  DEL_OTHERS_VISITED_VIEWS: (state, view) => {
    state.visitedViews = state.visitedViews.filter(v => {
      return v.meta.affix || v.path === view.path
    })
    persistState(state)
  },
  DEL_OTHERS_CACHED_VIEWS: (state, view) => {
    const index = state.cachedViews.indexOf(view.name)
    if (index > -1) {
      state.cachedViews = state.cachedViews.slice(index, index + 1)
    } else {
      // if index = -1, there is no cached tags
      state.cachedViews = []
    }
    persistState(state)
  },

  DEL_ALL_VISITED_VIEWS: state => {
    // keep affix tags
    const affixTags = state.visitedViews.filter(tag => tag.meta.affix)
    state.visitedViews = affixTags
    persistState(state)
  },
  DEL_ALL_CACHED_VIEWS: state => {
    state.cachedViews = []
    persistState(state)
  },
  CLEAR_ALL_VIEWS: state => {
    state.visitedViews = []
    state.cachedViews = []
    clearPersistedState()
  },

  UPDATE_VISITED_VIEW: (state, view) => {
    for (let i = 0; i < state.visitedViews.length; i++) {
      const v = state.visitedViews[i]
      if (v.path === view.path) {
        state.visitedViews.splice(i, 1, normalizeView(Object.assign({}, v, view)))
        persistState(state)
        break
      }
    }
  }
}

const actions = {
  addView({ dispatch }, view) {
    dispatch('addVisitedView', view)
    dispatch('addCachedView', view)
  },
  addVisitedView({ commit }, view) {
    commit('ADD_VISITED_VIEW', view)
  },
  addCachedView({ commit }, view) {
    commit('ADD_CACHED_VIEW', view)
  },

  delView({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delVisitedView', view)
      dispatch('delCachedView', view)
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },
  delVisitedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_VISITED_VIEW', view)
      resolve([...state.visitedViews])
    })
  },
  delCachedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_CACHED_VIEW', view)
      resolve([...state.cachedViews])
    })
  },

  delOthersViews({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delOthersVisitedViews', view)
      dispatch('delOthersCachedViews', view)
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },
  delOthersVisitedViews({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_OTHERS_VISITED_VIEWS', view)
      resolve([...state.visitedViews])
    })
  },
  delOthersCachedViews({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_OTHERS_CACHED_VIEWS', view)
      resolve([...state.cachedViews])
    })
  },

  delAllViews({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delAllVisitedViews', view)
      dispatch('delAllCachedViews', view)
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },
  delAllVisitedViews({ commit, state }) {
    return new Promise(resolve => {
      commit('DEL_ALL_VISITED_VIEWS')
      resolve([...state.visitedViews])
    })
  },
  delAllCachedViews({ commit, state }) {
    return new Promise(resolve => {
      commit('DEL_ALL_CACHED_VIEWS')
      resolve([...state.cachedViews])
    })
  },

  updateVisitedView({ commit }, view) {
    commit('UPDATE_VISITED_VIEW', view)
  },
  clearAllViews({ commit }) {
    commit('CLEAR_ALL_VIEWS')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
